import axios, { AxiosError, AxiosResponse } from "axios";
import { AnsweredQuestionWithId } from "@teamprevent/common/questionnaires/model";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { ConsultationDetails, parseConsultationModel } from "@teamprevent/common/consultations/models";
import { ReferralReportDetails } from "@teamprevent/common/referralReport/models";

import { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { parseTaskModel, CreateTaskDto } from "../task/model";
import { OhCaseDetailModel, parseOhCaseModel } from "../crud/ohCase/model";
import { mapCreateTaskDtoToServer } from "../crud/ohCase/ohCaseApiMappers";

const apiUrl = "/api/referralReport";

const load = async (ohCaseId: string, taskId: string): Promise<ReferralReportDetails | null> => {
    try {
        const response = await axios.get<ReferralReportDetails>(`${apiUrl}/${ohCaseId}/${taskId}`);
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError && axiosError.response && axiosError.response.status === 404) {
            return null;
        }

        throw ex;
    }
};

const loadPreview = async (ohCaseId: string, taskId: string): Promise<ReferralReportDetails | null> => {
    try {
        const response = await axios.get<ReferralReportDetails>(`${apiUrl}/preview/${ohCaseId}/${taskId}`);
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError && axiosError.response && axiosError.response.status === 404) {
            return null;
        }

        throw ex;
    }
};

const loadConsultation = async (ohCaseId: string, taskId: string): Promise<ConsultationDetails> => {
    const response = await axios.get<ServerModelWithDateStrings<ConsultationDetails>>(`${apiUrl}/${ohCaseId}/${taskId}/consultation`);
    return parseConsultationModel(response.data);
};

const loadConsultationPreview = async (ohCaseId: string, taskId: string): Promise<ConsultationDetails> => {
    const response = await axios.get<ServerModelWithDateStrings<ConsultationDetails>>(`${apiUrl}/preview/${ohCaseId}/${taskId}/consultation`);
    return parseConsultationModel(response.data);
};

const save = async (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string): Promise<ReferralReportDetails> => {
    try {
        return (await axios.post<ReferralReportDetails>(`${apiUrl}/${ohCaseId}/${taskId}`, { data: { questions }, eTag })).data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError.response?.status === 400) {
            const error = { message: "Could not save as a newer version exists", status: 400 };
            throw error;
        } else {
            throw ex;
        }
    }
};

const submit = async (ohCaseId: string, taskId: string, tasks: CreateTaskDto[]): Promise<OhCaseDetailModel> => {
    const createTasks = tasks.map(nextTask => mapCreateTaskDtoToServer(nextTask));

    const response = await axios.post<
        ServerModelWithDateStrings<OhCaseDetailModel>,
        AxiosResponse<ServerModelWithDateStrings<OhCaseDetailModel>>,
        ServerModelWithDateStrings<CreateTaskDto>[]>(`${apiUrl}/${ohCaseId}/${taskId}/successorTasks`, createTasks);

    return parseOhCaseModel(response.data);
};

const submitEdit = async (ohCaseId: string, taskId: string): Promise<OhCaseDetailModel> => {
    const response = await axios.post<
        ServerModelWithDateStrings<OhCaseDetailModel>,
        AxiosResponse<ServerModelWithDateStrings<OhCaseDetailModel>>,
        ServerModelWithDateStrings<CreateTaskDto>[]>(`${apiUrl}/${ohCaseId}/${taskId}/completeEdit`);

    return parseOhCaseModel(response.data);
};

const submitAmendment = async (ohCaseId: string, taskId: string): Promise<OhCaseDetailModel> => {
    const response = await axios.post<
        ServerModelWithDateStrings<OhCaseDetailModel>,
        AxiosResponse<ServerModelWithDateStrings<OhCaseDetailModel>>,
        ServerModelWithDateStrings<CreateTaskDto>[]>(`${apiUrl}/${ohCaseId}/${taskId}/completeAmendments`);

    return parseOhCaseModel(response.data);
};

const sendToEmployeeForConsent = async (ohCaseId: string, taskId: string, notes: string): Promise<OhCaseDetailModel> => {
    const response = await axios.post<ServerModelWithDateStrings<OhCaseDetailModel>>(
        `${apiUrl}/${ohCaseId}/${taskId}/sendToEmployeeForConsent`, { notes });

    return parseOhCaseModel(response.data);
};

const approve = async (ohCaseId: string, taskId: string, notes: string): Promise<OhCaseDetailModel> => {
    const response = await axios.post<ServerModelWithDateStrings<OhCaseDetailModel>>(
        `${apiUrl}/${ohCaseId}/${taskId}/approve`, { notes });

    return parseOhCaseModel(response.data);
};

const reject = async (ohCaseId: string, taskId: string, notes: string): Promise<TaskModel[]> => {
    const response = await axios.post<
        ServerModelWithDateStrings<TaskModel>[]
    >(`${apiUrl}/${ohCaseId}/${taskId}/reject`, { notes });
    return (response.data).map(parseTaskModel);
};

const requestChanges = async (ohCaseId: string, taskId: string, notes: string): Promise<TaskModel[]> => {
    const response = await axios.post<
        ServerModelWithDateStrings<TaskModel>[]
    >(`${apiUrl}/${ohCaseId}/${taskId}/requestChanges`, { notes });
    return (response.data).map(parseTaskModel);
};

const requestReportEdit = async (ohCaseId: string, taskId: string, reasonForEdit: string): Promise<TaskModel[]> => {
    const response = await axios.post<
        ServerModelWithDateStrings<TaskModel>[]
        >(`${apiUrl}/${ohCaseId}/${taskId}/requestReportEdit`, { reasonForEdit });
    return (response.data).map(parseTaskModel);
};

export const ReferralReportApi = {
    load,
    loadPreview,
    loadConsultation,
    loadConsultationPreview,
    save,
    submit,
    submitAmendment,
    submitEdit,
    approve,
    reject,
    requestChanges,
    requestReportEdit,
    sendToEmployeeForConsent
};
